// Tolerant language resolution for per-language template maps
// (html / texts / mailSubject, each keyed by a language code).
//
// Mirror of the worker's `main-consumer/src/lib/lang-resolve.ts` — kept in sync
// by hand (no shared package between repos). Background: guest.language and
// template map keys are a mix of short ("de") and full ("de-DE") codes, so an
// exact-match lookup silently falls through to the primary language whenever
// the two granularities disagree. This resolver tries a small candidate chain
// (exact → base → region-collapse) in both directions so "de" and "de-DE"
// interoperate.

/**
 * Expand a language code into the ordered list of keys we try against a map.
 *   "de-DE" → ["de-DE", "de-de", "de"]
 *   "de"    → ["de", "de-DE"]   (region-collapse, so a short-code value matches a full-locale key)
 */
export function langCandidates(code: string | null | undefined): string[] {
  if (typeof code !== "string") return [];
  const c = code.trim();
  if (c.length === 0) return [];

  const out: string[] = [];
  const push = (v: string): void => {
    if (v.length > 0 && !out.includes(v)) out.push(v);
  };

  push(c);
  push(c.toLowerCase());

  const base = c.toLowerCase().split("-")[0]!;
  push(base);
  push(`${base}-${base.toUpperCase()}`);

  return out;
}

/**
 * The first map KEY that speaks the same base language as `code`, or undefined.
 *
 * The region-collapse in {@link langCandidates} only PRETENDS to cover this: it
 * guesses `${base}-${BASE}`, which is a real locale for de/fr/it/es/nl/pt and
 * nonsense everywhere else. Measured 2026-08-21 against the cockpit's own
 * dropdown (84 base languages): 58 collapse to a locale that does not exist.
 * Harmless for the single-variant ones — guest and template both carry it, so
 * the exact match fires. The damage is in multi-variant languages, English
 * worst of all, because `en-EN` is not a locale:
 *
 *     "en-US" against keys {de-DE, en-GB}, primary de-DE
 *       candidates [en-US, en-us, en, en-EN] -> no hit -> GERMAN
 *
 * Same step, deliberately the same wording, as the base-language fallback that
 * `resolveLangKey(available[])` has always had — that takes a LIST of tags,
 * this takes the keys of a map. Keys are sorted so the pick cannot depend on
 * which variant happened to be stored first.
 */
function baseLanguageKey(
  map: Record<string, string>,
  code: string | null | undefined,
): string | undefined {
  const base = baseOf(code);
  if (base.length === 0) return undefined;
  for (const key of Object.keys(map).sort()) {
    if (baseOf(key) !== base) continue;
    const v = map[key];
    if (typeof v === "string" && v.length > 0) return key;
  }
  return undefined;
}

/**
 * The base language of a tag ("de-DE" → "de"), trimmed and folded.
 *
 * Exported because callers outside this module need the SAME notion of "same
 * language" — a hand-rolled `code.toLowerCase().split("-")[0]` next door drops
 * the `.trim()`, and a guest language of `" de-DE "` (the public
 * `/data-input/submit` door stores whatever it receives) then reads as a
 * different language from `de-DE`.
 */
export function baseOf(code: string | null | undefined): string {
  if (typeof code !== "string") return "";
  return code.trim().toLowerCase().split("-")[0] ?? "";
}
/** The builder's right-to-left languages, by base (default-returner.ts ALL_LANGUAGE_OPTIONS: ar-SA, fa-IR, he-IL, ps-AF, ur-PK). */
const RTL_BASES: ReadonlySet<string> = new Set(["ar", "fa", "he", "ps", "ur"]);

export type TextDirection = "ltr" | "rtl";

/**
 * The writing direction of the text a language KEY stands for (plan Rev. 4
 * W-A.6). Pass the key `resolveLangKeyFromMap` picked, never the requested
 * language: an ar-SA variant without words of its own shows the primary
 * language's text and stays ltr. Unknown or empty → ltr.
 */
export const textDirectionFor = (langKey: string | null | undefined): TextDirection =>
  RTL_BASES.has(baseOf(langKey)) ? "rtl" : "ltr";


/**
 * Resolve the best value from a per-language map for the given language,
 * falling back to `primary`, then any non-empty value, then `fallback`.
 * Empty-string entries are treated as missing.
 */
export function resolveLangValue(
  map: Record<string, string> | undefined | null,
  lang: string | null | undefined,
  primary: string | null | undefined,
  fallback: string,
): string {
  if (map === undefined || map === null) return fallback;

  const lookup = (keys: string[]): string | undefined => {
    for (const key of keys) {
      const v = map[key];
      if (typeof v === "string" && v.length > 0) return v;
    }
    return undefined;
  };

  const byLang = lookup(langCandidates(lang));
  if (byLang !== undefined) return byLang;

  // Any variant of the requested language before dropping to a different
  // language entirely — see baseLanguageKey.
  const byBase = baseLanguageKey(map, lang);
  if (byBase !== undefined) return map[byBase]!;

  const byPrimary = lookup(langCandidates(primary));
  if (byPrimary !== undefined) return byPrimary;
  const byPrimaryBase = baseLanguageKey(map, primary);
  if (byPrimaryBase !== undefined) return map[byPrimaryBase]!;

  for (const v of Object.values(map)) {
    if (typeof v === "string" && v.length > 0) return v;
  }
  return fallback;
}

/**
 * Pick the best matching language TAG from a list of available tags for the
 * given language, using the same candidate chain. Falls back to `primary`,
 * then the first available tag. Used to choose which language a preview /
 * send should render when a recipient language is known.
 *
 * After the exact candidate chain, falls back to a base-language match so a
 * region variant resolves to a different region of the same language (e.g. a
 * visitor's "en-US" matches an available "en-GB" instead of dropping to the
 * primary language).
 */
export function resolveLangKey(
  available: string[],
  lang: string | null | undefined,
  primary: string,
): string {
  const has = (key: string): boolean => available.includes(key);

  for (const cand of langCandidates(lang)) {
    if (has(cand)) return cand;
  }

  // Base-language fallback: "en-US" → first available "en-*".
  const baseOf = (code: string | null | undefined): string =>
    typeof code === "string" ? (code.toLowerCase().split("-")[0] ?? "") : "";
  const langBase = baseOf(lang);
  if (langBase.length > 0) {
    const byBase = available.find((a) => baseOf(a) === langBase);
    if (byBase !== undefined) return byBase;
  }

  for (const cand of langCandidates(primary)) {
    if (has(cand)) return cand;
  }
  return available.includes(primary) ? primary : (available[0] ?? primary);
}

/**
 * Which KEY of a per-language content map the recipient's mail will actually be
 * rendered from — the map-based companion to {@link resolveLangValue}, and the
 * one to use whenever the answer is a claim about what someone RECEIVES.
 *
 * Ported verbatim from `main-consumer/src/lib/lang-resolve.ts` (`resolveLangKey`),
 * which is what the worker runs. {@link resolveLangKey} above answers a
 * different question — "which of these offered tags" — and is right for a
 * picker's option list, but it was being used for this one too, where it is
 * wrong four ways:
 *
 *   1. it takes tags, so an empty variant looks like a real one (the recipient
 *      is promised English and receives the primary language),
 *   2. its base match runs in document order while the worker sorts, so an
 *      `en-CA` guest is promised whichever of en-US/en-GB happens to be stored
 *      first and receives the other,
 *   3. it has no base step for the PRIMARY, so a template keyed {en-GB, de-AT}
 *      with primary de-DE resolves to English where the worker sends de-AT,
 *   4. it invents `primary` for an empty map where the worker returns null and
 *      the send ends up as a "No content available" placeholder.
 *
 * Keep this function byte-equivalent to the worker's. The two files are hand-
 * synced — that is exactly how they drifted apart.
 */
export function resolveLangKeyFromMap(
  map: Record<string, string> | undefined | null,
  lang: string | null | undefined,
  primary: string | null | undefined,
): string | null {
  if (map === undefined || map === null) return null;

  const lookupKey = (keys: string[]): string | undefined => {
    for (const key of keys) {
      const v = map[key];
      if (typeof v === "string" && v.length > 0) return key;
    }
    return undefined;
  };

  const byLang = lookupKey(langCandidates(lang));
  if (byLang !== undefined) return byLang;

  const byBase = baseLanguageKey(map, lang);
  if (byBase !== undefined) return byBase;

  const byPrimary = lookupKey(langCandidates(primary));
  if (byPrimary !== undefined) return byPrimary;
  const byPrimaryBase = baseLanguageKey(map, primary);
  if (byPrimaryBase !== undefined) return byPrimaryBase;

  for (const [k, v] of Object.entries(map)) {
    if (typeof v === "string" && v.length > 0) return k;
  }
  return null;
}
